import { useState, useEffect } from 'react';
import {
  Button,
  ButtonSet,
  Slider,
  Tooltip,
  ModalWrapper,
} from 'carbon-components-react';
import { FormattedMessage } from 'react-intl';
import Cookies from 'js-cookie';
import Helmet from 'react-helmet';
import './RPAROIEstimator.scss';
import './NoteDrawer.scss';
import { Close32 } from '@carbon/icons-react';
import 'src/common/style/cds-data.scss';
import 'src/common/style/importantStyle.scss';
import 'src/common/style/common.scss';
import { segmentTracking } from '../lib/tracker';
import NoteDrawer from './NoteDrawer';
import Thanks from './Thanks';
import { useEnv } from 'src/common/hooks/widgets/useEnv';
import { useBPName } from 'src/common/hooks/widgets/useBPName';
import { useCurrency } from 'src/common/hooks/widgets/useCurrency';
import { useSmallBreakpoint } from 'src/common/hooks/widgets/useBreakpoint';
import * as URX_FORM from '../constants/urx-form';
import { swapCountryAndLanguage } from 'src/common/utils/swapCountryAndLanguage';
import {
  NumberOfHoursPerWeek,
  FullTimeEmployeeCost,
  ErrorRate,
  NumberOfBusinessProcessToAutomate,
  limits,
  UT30RPAROI,
} from '../constants/defaults';
import messages from '../locales/messages';
import { IBMLocale } from 'src/common/locale/mapValidLocale';
import { setROI } from '../hooks/useRoi';
import {
  determineCountry,
  LangDefaults,
  useLang,
  getMessage,
} from 'src/common/hooks/widgets/useLang';
import countryList from '../data/currencies.json';
import Footnote from './Footnote';
import { urxScript } from 'src/common/hooks/widgets/globalDefinitions';

export interface RPAROIEstimatorProps {
  langCode: string;
  test: boolean;
  countryToTest: string;
}

const RPAROIEstimator = ({
  langCode,
  test,
  countryToTest,
}: RPAROIEstimatorProps) => {
  const localeCode = swapCountryAndLanguage(langCode as IBMLocale);

  const [, setShowContactUs] = useState(true);
  const [invalidNHWMax, setInvalidNHWMax] = useState(false);
  const [invalidNHWMin, setInvalidNHWMin] = useState(false);
  const [invalidFTECMax, setInvalidFTECMax] = useState(false);
  const [invalidFTECMin, setInvalidFTECMin] = useState(false);
  const [invalidErrorRateMax, setInvalidErrorRateMax] = useState(false);
  const [invalidErrorRateMin, setInvalidErrorRateMin] = useState(false);

  const [errorRate, setErrorRate] = useState(ErrorRate.default);
  const [currentFullTimeEmployeeCost, setCurrentFullTimeEmployeeCost] =
    useState(FullTimeEmployeeCost.default);
  const [currentNumberOfHoursPerWeek, setCurrentNumberOfHoursPerWeek] =
    useState(NumberOfHoursPerWeek.default);
  const [numberOfProcessToAutomate, setNumberOfProcessToAutomate] = useState(
    NumberOfBusinessProcessToAutomate.default,
  );
  const [
    invalidNumberOfProcessesToAutomateMax,
    setInvalidNumberOfProcessesToAutomateMax,
  ] = useState(false);
  const [
    invalidNumberOfProcessesToAutomateMin,
    setInvalidNumberOfProcessesToAutomateMin,
  ] = useState(false);

  const [formComplete, setFormComplete] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const isSmall = useSmallBreakpoint('560px');
  const { bpName } = useBPName(UT30RPAROI, true);
  const [currency, setCurrency] = useState('');
  const [, setCountry] = useState('');

  const [lang, userLocale] = useLang(
    countryList,
    test,
    countryToTest,
    setCurrency,
    setCountry,
    localeCode,
  );
  // Set us-en
  const [, setUrxLang] = useState(LangDefaults.localCountryLang as IBMLocale);
  const [toggleCountry, setToggleCountry] = useState(false);
  const [modalLocalCountry, setModalLocalCountry] = useState('');
  // Set USD
  const [modalLocalCurrency, setModalLocalCurrency] = useState(
    LangDefaults.localCurrency,
  );
  // Set English language
  const [modalLocalLanguage, setModalLocalLanguage] = useState(
    LangDefaults.CountryLanguage,
  );
  // ROI
  const [roiValue, setRoiValue] = useCurrency(currency, userLocale, 0);
  const [recommendedUnattendedBots, setRecommendedUnattendedBots] = useState(0);
  const [recommendedAttendedBots, setRecommendedAttendedBots] = useState(0);
  const [recommendedEnvironments, setRecommendedEnvironments] = useState(0);
  const [roiPercentage, setROIPercentage] = useState(0);
  const [monthlyTimeSavings, setMonthlyTimeSavings] = useState(0);
  const [reworkSavings, setReworkSavings] = useCurrency(
    currency,
    userLocale,
    0,
  );
  const [monthlyPrice, setMonthlyPrice] = useCurrency(currency, userLocale, 0);
  const [cost, setCost] = useState(0);

  window.onUrxFormSubmitSuccess = () => {
    setFormComplete(!formComplete);
    setDrawerOpen(false); // Hide Drawer on Thank you page
  };

  const [localExist, setLocalExist] = useState(false);
  let localStorageFound = localStorage.getItem('langLocale');
  if (localStorageFound == null)
    localStorageFound = LangDefaults.localCountryLang;
  const [selectedLocale, setSelectedLocale] = useState(localStorageFound);
  localStorage.setItem('showRPAForm', 'false');

  // Determine Country
  determineCountry(
    setLocalExist,
    setSelectedLocale,
    setModalLocalCountry,
    setModalLocalLanguage,
    setModalLocalCurrency,
    setToggleCountry,
    lang,
    countryList,
    false,
  );

  /**
   * Define validation of the Input field of the sliders
   */
  useEffect(() => {
    const nhwId = document.getElementById('NHW-Id-input-for-slider');
    const ftecId = document.getElementById('FTEC-Id-input-for-slider');
    const erId = document.getElementById('ER-Id-input-for-slider');
    const nbpaId = document.getElementById('NBPA-Id-input-for-slider');

    /**
     * Define Listener
     */
    const addListener = (
      id: HTMLElement | null,
      setMax: React.Dispatch<React.SetStateAction<boolean>>,
      setMin: React.Dispatch<React.SetStateAction<boolean>>,
      max: number,
      min: number,
      setvalue: React.Dispatch<React.SetStateAction<number>>,
    ) => {
      if (id !== null) {
        id.addEventListener('input', (e) => {
          if (e != null) {
            const element = e.currentTarget as HTMLInputElement;
            if (element.value !== '') {
              if (Number.parseInt(element.value) > max) {
                element.value = max.toString();
                setMax(true);
                setvalue(max);
              } else if (Number.parseInt(element.value) < min) {
                element.value = min.toString();
                element.value = ' ';
                setvalue(min);
                setMin(true);
              }
            } else {
              element.value = ' ';
              setMin(true);
            }
          }
        });
      }
    };

    /**
     * Current number of on-premises host
     */
    addListener(
      nhwId,
      setInvalidNHWMax,
      setInvalidNHWMin,
      NumberOfHoursPerWeek.max,
      NumberOfHoursPerWeek.min,
      setCurrentNumberOfHoursPerWeek,
    );

    /**
     * Current monthly cloud bill
     */
    addListener(
      ftecId,
      setInvalidFTECMax,
      setInvalidFTECMin,
      FullTimeEmployeeCost.max,
      FullTimeEmployeeCost.min,
      setCurrentFullTimeEmployeeCost,
    );

    /**
     * Current cost per on-premises host
     */
    addListener(
      erId,
      setInvalidErrorRateMax,
      setInvalidErrorRateMin,
      ErrorRate.max,
      ErrorRate.min,
      setErrorRate,
    );

    /**
     * Annual growth rate on-prem VM
     */
    addListener(
      nbpaId,
      setInvalidNumberOfProcessesToAutomateMax,
      setInvalidNumberOfProcessesToAutomateMin,
      NumberOfBusinessProcessToAutomate.max,
      NumberOfBusinessProcessToAutomate.min,
      setNumberOfProcessToAutomate,
    );
  }, []);

  /**
   * Read and set defaults from ROI
   */
  const startTxnProcessor = () => {
    try {
      setTimeout(async () => {
        const showBenefit = localStorage.getItem('showRPAForm');
        if (showBenefit !== null) {
          if (showBenefit === 'true') {
          }
        }
        // Check inputs from PE
        const priceEstimatorId = localStorage.getItem('pe-Id');
        if (priceEstimatorId !== null) {
          const value = Number.parseInt(priceEstimatorId);
          setMonthlyPrice(value);
        }
        startTxnProcessor();
      }, limits.secs * 1000);
    } catch (err) {
      console.error(`Error while timeout: ${err}`);
    }
  };

  startTxnProcessor();

  /**
   * Get estimated price from Price Estimator widget
   */
  useEffect(() => {
    const priceEstimatorId = localStorage.getItem('pe-Id');
    if (priceEstimatorId !== null) {
      const value = Number.parseInt(priceEstimatorId);
      setCost(value);
    }
  }, [monthlyPrice]);

  /**
   *
   */
  useEffect(() => {
    setCookie();
    localStorage.setItem(
      'roiAttendedRobots-Id',
      recommendedAttendedBots.toString(),
    );
    localStorage.setItem(
      'roiUnattendedBots-Id',
      recommendedUnattendedBots.toString(),
    );
    localStorage.setItem(
      'roiNumberEnvironments-Id',
      recommendedEnvironments.toString(),
    );
  }, [
    numberOfProcessToAutomate,
    currentNumberOfHoursPerWeek,
    currentFullTimeEmployeeCost,
    errorRate,
    roiValue,
    setRecommendedAttendedBots,
    setRecommendedUnattendedBots,
    setRecommendedEnvironments,
  ]);

  /**
   * Call to start the pricing
   */
  setROI(
    numberOfProcessToAutomate,
    currentNumberOfHoursPerWeek,
    currentFullTimeEmployeeCost,
    errorRate,
    setShowContactUs,
    setRoiValue,
    setRecommendedAttendedBots,
    setRecommendedUnattendedBots,
    setRecommendedEnvironments,
    setROIPercentage,
    setMonthlyTimeSavings,
    setReworkSavings,
    cost,
  );

  /**
   *
   */
  const setCookie = () => {
    const cookieContent =
      ' Number of business process to automate in the first year: ' +
      numberOfProcessToAutomate +
      ' | Current number of hours per week:' +
      currentNumberOfHoursPerWeek +
      ' | Current full time employee cost: ' +
      currentFullTimeEmployeeCost +
      ' | Current error rate: ' +
      errorRate +
      ' | ROI: ' +
      roiValue +
      ' | Request a quote -IBM RPA ROI Estimator-' +
      ' | FormId:' +
      URX_FORM.FORM_ID;
    const text = JSON.stringify({
      source: URX_FORM.FORM_ID,
      // One field to provide all values (stringify)
      dcq: {
        Q_NOTESDATA: cookieContent,
        Q_ASSET_NAME: 'IBM RPA ROI Estimator', // ISC - Asset Name
      },
    });
    const jsonCookie = {
      message: cookieContent,
      content: {
        currentNumberOfHoursPerWeek: currentNumberOfHoursPerWeek,
        currentFullTimeEmployeeCost: currentFullTimeEmployeeCost,
        errorRate: errorRate,
        numberOfProcessToAutomate: numberOfProcessToAutomate,
        estimate: roiValue,
      },
    };
    localStorage.setItem('RPA-ROI-formContent', JSON.stringify(jsonCookie));
    Cookies.set('urxdcq', text);
  };

  /* Have urx form deault in us-en when on language us page */
  useEffect(() => {
    const fetchedUrxLang = localeCode; // us-en
    setUrxLang(userLocale as IBMLocale); // de-de
    if (fetchedUrxLang !== lang) {
      setUrxLang(fetchedUrxLang as IBMLocale); // default us-en
    }
  });

  const handleSliders = (
    value: number,
    setValue: React.Dispatch<React.SetStateAction<number>>,
    max: number,
    min: number,
    setMax: React.Dispatch<React.SetStateAction<boolean>>,
    setMin: React.Dispatch<React.SetStateAction<boolean>>,
  ) => {
    if (value >= min && value <= max) {
      setValue(value);
      setMax(false);
      setMin(false);
    } else if (value > max) {
      setValue(max);
      setMax(true);
    } else {
      setMin(true);
      setValue(min);
    }
  };

  return (
    <section id="RPA_ROI" className="widget-styles">
      <div className="RPA_ROI dsh-widget widget-reset">
        <Helmet>
          <script src={urxScript}></script>
        </Helmet>
        {formComplete ? (
          <Thanks
            numberOfProcessToAutomate={numberOfProcessToAutomate}
            currentNumberOfHoursPerWeek={currentNumberOfHoursPerWeek}
            currentFullTimeEmployeeCost={currentFullTimeEmployeeCost}
            roiValue={roiValue}
            errorRate={errorRate}
            bpName={bpName}
            bpConsentMail={false}
          ></Thanks>
        ) : (
          // Not Form completed
          <div className="bx--grid" id="RPAROIGrid">
            <div className="bx--row mar3">
              <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-4">
                <div className="bx--row">
                  <div className="bx--col-lg-4 bx--col-md-8 bx--col-sm-8 mar1 bx--offset-lg-12">
                    {localExist && toggleCountry ? (
                      <ModalWrapper
                        modalLabel={` ${getMessage(
                          messages.foundCountry,
                        )} ${modalLocalCountry}.
                          ${getMessage(
                            messages.accessPage,
                          )} ${modalLocalLanguage} ${modalLocalCurrency}`}
                        modalHeading={`${getMessage(messages.availableIn)}
                          ${modalLocalLanguage} ${getMessage(
                          messages.localCurrencie,
                        )} ${modalLocalCurrency}`}
                        size={'sm'}
                        primaryButtonText={`${getMessage(
                          messages.takeMeThere,
                        )}`}
                        secondaryButtonText={`${getMessage(
                          messages.stayHere,
                        )} `}
                        buttonTriggerText={` ${getMessage(
                          messages.accessPageCapitalized,
                        )} ${modalLocalLanguage}`}
                        handleOpen={() => {
                          segmentTracking('CTA Clicked', {
                            pageTitle: 'RPA Pricing Page',
                            object: 'Pricing page',
                            resultValue: 'CTA clicked',
                            CTA: 'Modal open ' + modalLocalCountry,
                            location: 'ROI Estimator',
                            action: '',
                            field: '',
                          });
                        }}
                        handleSubmit={() => {
                          localStorage.setItem('langLocale', selectedLocale);
                          (window.location.href =
                            'https://www.ibm.com/' +
                            selectedLocale +
                            '/products/robotic-process-automation/pricing'),
                            segmentTracking('CTA Clicked', {
                              pageTitle: 'RPA Pricing Page',
                              object: 'Pricing page',
                              resultValue: 'CTA clicked',
                              CTA: 'Switched to country ' + modalLocalCountry,
                              location: 'ROI Estimator',
                              action: '',
                              field: '',
                            });
                          return true;
                        }}
                        onSecondarySubmit={() => {
                          segmentTracking('CTA Clicked', {
                            pageTitle: 'RPA Pricing Page',
                            object: 'Pricing page',
                            resultValue: 'CTA clicked',
                            CTA:
                              'Stay here button clicked ' + modalLocalCountry,
                            location: 'ROI Estimator',
                            action: '',
                            field: '',
                          });
                          setToggleCountry(false);
                          return true;
                        }}
                      ></ModalWrapper>
                    ) : null}
                  </div>
                </div>
                <div className="bx--row">
                  <div className="bx--col-lg-9 bx--col-md-16 bx--col-sm-16">
                    {/* Titles */}
                    <div className="bx--row">
                      <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                        <p className="page_sub_heading">
                          <FormattedMessage {...messages.widgetText_1} />
                        </p>
                        <p className="page_body padding_24_3 mar7">
                          <FormattedMessage {...messages.widgetInfo} />
                          {` `}
                          <FormattedMessage {...messages.widgetText_2} />
                        </p>
                      </div>
                    </div>
                    {/*  Tooltip: Number of business process to automate */}
                    <div className="bx--row">
                      <div className="bx--col-lg-16 bx--col-md-6 bx--col-sm-4">
                        <Tooltip
                          onChange={() => {
                            segmentTracking('CTA Clicked', {
                              pageTitle: 'RPA Pricing Page',
                              object: 'Pricing Page',
                              resultValue: 'CTA clicked',
                              CTA: 'Number of business process to automate tooltip',
                              location: 'ROI Estimator',
                              action: '',
                              field: '',
                            });
                          }}
                          direction={isSmall ? 'left' : 'bottom'}
                          tabIndex={0}
                          triggerText={
                            <span className="page_body">
                              <FormattedMessage
                                {...messages.numberOfBusinessProcessToAutomate}
                              />
                            </span>
                          }
                        >
                          <p className="slide_text">
                            <FormattedMessage
                              {...messages.numberOfBusinessProcessToAutomateHeadTooltip}
                            />
                            <br />
                            <FormattedMessage
                              {...messages.numberOfBusinessProcessToAutomateDescTooltip}
                            />
                          </p>
                        </Tooltip>
                      </div>
                    </div>
                    {/*  Slider: Number of business process to automate */}
                    <div className="bx--row">
                      <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                        <Slider
                          id="NBPA-Id"
                          max={NumberOfBusinessProcessToAutomate.max}
                          min={NumberOfBusinessProcessToAutomate.min}
                          value={numberOfProcessToAutomate}
                          inputType={'number'}
                          invalid={
                            invalidNumberOfProcessesToAutomateMax ||
                            invalidNumberOfProcessesToAutomateMin
                          }
                          onBlur={() => {
                            null;
                          }}
                          labelText={
                            invalidNumberOfProcessesToAutomateMax == true ? (
                              <span className="slide_text">
                                <FormattedMessage
                                  {...messages.invalidVmText_1}
                                />
                                {` ${NumberOfBusinessProcessToAutomate.max}`}
                                <FormattedMessage
                                  {...messages.invalidVmText_2}
                                />
                              </span>
                            ) : invalidNumberOfProcessesToAutomateMin ==
                              true ? (
                              <span className="slide_text">
                                <FormattedMessage
                                  {...messages.invalidVmText_3}
                                />
                                {` ${NumberOfBusinessProcessToAutomate.min}`}
                                <FormattedMessage
                                  {...messages.invalidVmText_2}
                                />
                              </span>
                            ) : null
                          }
                          onChange={({ value }) => {
                            handleSliders(
                              value,
                              setNumberOfProcessToAutomate,
                              NumberOfBusinessProcessToAutomate.max,
                              NumberOfBusinessProcessToAutomate.min,
                              setInvalidNumberOfProcessesToAutomateMax,
                              setInvalidNumberOfProcessesToAutomateMin,
                            );
                            segmentTracking('CTA Clicked', {
                              pageTitle: 'RPA Pricing Pagee',
                              object: 'Pricing Page',
                              resultValue: 'CTA clicked',
                              CTA: 'Number of business process to automate slider',
                              location: 'ROI Estimator',
                              action: '',
                              field: '',
                            });
                          }}
                        ></Slider>
                      </div>
                    </div>
                    {/* Tooltip: Number of hours per week */}
                    <div className="bx--row mar3">
                      <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                        <Tooltip
                          onChange={() => {
                            segmentTracking('CTA Clicked', {
                              pageTitle: 'RPA Pricing Page',
                              object: 'Pricing Page',
                              resultValue: 'CTA clicked',
                              CTA: 'Number of hours per week tooltip',
                              location: 'ROI Estimator',
                              action: '',
                              field: '',
                            });
                          }}
                          direction={'bottom'}
                          tabIndex={0}
                          triggerText={
                            <span className="page_body">
                              <FormattedMessage
                                {...messages.numberOfHoursPerWeekLabel}
                              />
                            </span>
                          }
                        >
                          <p className="slide_text">
                            <FormattedMessage
                              {...messages.numberOfHoursPerWeekHeadTooltip}
                            />
                            <br />
                            <FormattedMessage
                              {...messages.numberOfHoursPerWeekDescTooltip}
                            />
                          </p>
                        </Tooltip>
                      </div>
                    </div>
                    {/* Slider: Number of hours per week*/}
                    <div className="bx--row">
                      <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                        <Slider
                          id="NHW-Id"
                          min={NumberOfHoursPerWeek.min}
                          max={NumberOfHoursPerWeek.max}
                          inputType={'number'}
                          value={currentNumberOfHoursPerWeek}
                          invalid={invalidNHWMax || invalidNHWMin}
                          labelText={
                            invalidNHWMax == true ? (
                              <span className="slide_text">
                                <FormattedMessage
                                  {...messages.invalidVmText_1}
                                />
                                {` ${NumberOfHoursPerWeek.max}`}
                                <FormattedMessage
                                  {...messages.invalidVmText_2}
                                />
                              </span>
                            ) : invalidNHWMin == true ? (
                              <span className="slide_text">
                                <FormattedMessage
                                  {...messages.invalidVmText_3}
                                />
                                {` ${NumberOfHoursPerWeek.min}`}
                                <FormattedMessage
                                  {...messages.invalidVmText_2}
                                />
                              </span>
                            ) : null
                          }
                          onBlur={() => {
                            null;
                          }}
                          onChange={({ value }) => {
                            handleSliders(
                              value,
                              setCurrentNumberOfHoursPerWeek,
                              NumberOfHoursPerWeek.max,
                              NumberOfHoursPerWeek.min,
                              setInvalidNHWMax,
                              setInvalidNHWMin,
                            );
                            segmentTracking('CTA Clicked', {
                              pageTitle: 'RPA Pricing Page',
                              object: 'Pricing Page',
                              resultValue: 'CTA clicked',
                              CTA: 'Number of hours per week slider',
                              location: 'ROI Estimator',
                              action: '',
                              field: '',
                            });
                          }}
                        />
                      </div>
                    </div>
                    {/*  Tooltip: Fulll time employee cost*/}
                    <div className="bx--row mar3">
                      <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                        <Tooltip
                          onChange={() => {
                            segmentTracking('CTA Clicked', {
                              pageTitle: 'RPA Pricing Page',
                              object: 'Pricing Page',
                              resultValue: 'CTA clicked',
                              CTA: 'Full time employee cost tooltip',
                              location: 'ROI Estimator',
                              action: '',
                              field: '',
                            });
                          }}
                          direction={'bottom'}
                          tabIndex={0}
                          triggerText={
                            <span className="page_body">
                              <FormattedMessage
                                {...messages.currentFullTimeEmployeeCostlabel}
                              />
                            </span>
                          }
                        >
                          <p className="slide_text">
                            <FormattedMessage
                              {...messages.fullTimeEmployeeCostHeadTooltip}
                            />
                            <br />
                            <FormattedMessage
                              {...messages.fullTimeEmployeeCostDescTooltip}
                            />
                          </p>
                        </Tooltip>
                      </div>
                    </div>
                    {/*  Slider: Current full time employee cost */}
                    <div className="bx--row mar1 show_k_label">
                      <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                        <Slider
                          id="FTEC-Id"
                          min={FullTimeEmployeeCost.min}
                          max={FullTimeEmployeeCost.max}
                          value={currentFullTimeEmployeeCost}
                          inputType={'number'}
                          invalid={invalidFTECMax || invalidFTECMin}
                          labelText={
                            invalidFTECMax == true ? (
                              <span className="slide_text">
                                <FormattedMessage
                                  {...messages.invalidVmText_1}
                                />
                                {` ${FullTimeEmployeeCost.max}`}
                                <FormattedMessage
                                  {...messages.invalidVmText_2}
                                />
                              </span>
                            ) : invalidFTECMin == true ? (
                              <span className="slide_text">
                                <FormattedMessage
                                  {...messages.invalidVmText_3}
                                />
                                {` ${FullTimeEmployeeCost.min}`}
                                <FormattedMessage
                                  {...messages.invalidVmText_2}
                                />
                              </span>
                            ) : null
                          }
                          onBlur={() => {
                            null;
                          }}
                          onChange={({ value }) => {
                            handleSliders(
                              value,
                              setCurrentFullTimeEmployeeCost,
                              FullTimeEmployeeCost.max,
                              FullTimeEmployeeCost.min,
                              setInvalidFTECMax,
                              setInvalidFTECMin,
                            );
                            segmentTracking('CTA Clicked', {
                              pageTitle: 'RPA Pricing Page',
                              object: 'Pricing Page',
                              resultValue: 'CTA clicked',
                              CTA: 'Full time employee cost slider',
                              location: 'ROI Estimator',
                              action: '',
                              field: '',
                            });
                          }}
                        />
                      </div>
                    </div>
                    {/*  Tooltip: Error rate */}
                    <div className="bx--row">
                      <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                        <Tooltip
                          onChange={() => {
                            segmentTracking('CTA Clicked', {
                              pageTitle: 'RPA Pricing Page',
                              object: 'Pricing Page',
                              resultValue: 'CTA clicked',
                              CTA: 'Error rate tooltip',
                              location: 'ROI Estimator',
                              action: '',
                              field: '',
                            });
                          }}
                          direction={isSmall ? 'right' : 'bottom'}
                          tabIndex={0}
                          triggerText={
                            <span className="page_body">
                              <FormattedMessage {...messages.errorRateLabel} />
                            </span>
                          }
                        >
                          <p className="slide_text">
                            <FormattedMessage
                              {...messages.errorrateHeadTooltip}
                            />
                            <br />
                            <FormattedMessage
                              {...messages.ErrorRateDescTooltip}
                            />
                          </p>
                        </Tooltip>
                      </div>
                    </div>
                    {/*  Slider: Error rate */}
                    <div className="bx--row mar1 show_percentage_label">
                      <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                        <Slider
                          id="ER-Id"
                          min={ErrorRate.min}
                          max={ErrorRate.max}
                          value={errorRate}
                          inputType={'number'}
                          onBlur={() => {
                            null;
                          }}
                          invalid={invalidErrorRateMax || invalidErrorRateMin}
                          labelText={
                            invalidErrorRateMax == true ? (
                              <span className="slide_text">
                                <FormattedMessage
                                  {...messages.invalidVmText_1}
                                />
                                {` ${ErrorRate.max}`}
                                <FormattedMessage
                                  {...messages.invalidVmText_2}
                                />
                              </span>
                            ) : invalidErrorRateMin == true ? (
                              <span className="slide_text">
                                <FormattedMessage
                                  {...messages.invalidVmText_3}
                                />
                                {` ${ErrorRate.min}`}
                                <FormattedMessage
                                  {...messages.invalidVmText_2}
                                />
                              </span>
                            ) : null
                          }
                          onChange={({ value }) => {
                            handleSliders(
                              value,
                              setErrorRate,
                              ErrorRate.max,
                              ErrorRate.min,
                              setInvalidErrorRateMax,
                              setInvalidErrorRateMin,
                            );

                            segmentTracking('CTA Clicked', {
                              pageTitle: 'RPA Pricing Page',
                              object: 'Pricing Page',
                              resultValue: 'CTA clicked',
                              CTA: 'Error rate slider',
                              location: 'ROI Estimator',
                              action: '',
                              field: '',
                            });
                          }}
                        />
                      </div>
                    </div>
                    <div id="footnote" className="div-only-lg mar2">
                      <div className="bx--row">
                        <div className="bx--col-lg-16">
                          <Footnote numNote={2} />
                          <div className="RPA_ROI_FN__footnote_text">
                            <FormattedMessage {...messages.footnoteOne2} />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* Navigation displaying results  */}
                  <div className="bx--col-lg-1"></div>
                  <div className="bx--col-lg-5 bx--col-md-16 bx--col-sm-16">
                    <div className="bx--row" id="stickyNav">
                      <div className="RPA_ROI__sticky-nav price_table_comp">
                        {/* Condition if above ceratin value to conta-us button */}

                        {/* Table with results  */}
                        <table className="cds--data-table cds--data-table--sm">
                          <thead>
                            <tr>
                              <th scope="col" colSpan={2}>
                                <div className="price_table_comp__heading">
                                  <FormattedMessage
                                    {...messages.resultsHeader}
                                  />
                                </div>
                              </th>
                              <th> </th>
                            </tr>
                          </thead>
                          <tbody aria-live="polite">
                            <tr>
                              <td colSpan={2}>
                                <div className="price_table_comp__requirement price_table_comp_color padding_8">
                                  <FormattedMessage
                                    {...messages.attendedBots}
                                  />
                                </div>
                              </td>
                              <td></td>
                              <td>
                                <div className="price_table_comp__quantity padding_8">{`${recommendedAttendedBots}`}</div>
                              </td>
                            </tr>
                            <tr>
                              <td colSpan={2}>
                                <div className="price_table_comp__requirement price_table_comp_color padding_8">
                                  <FormattedMessage
                                    {...messages.unattendedBots}
                                  />
                                </div>
                              </td>
                              <td></td>
                              <td>
                                <div className="price_table_comp__quantity padding_8">{`${recommendedUnattendedBots}`}</div>
                              </td>
                            </tr>
                            <tr>
                              <td colSpan={2}>
                                <div className="price_table_comp__requirement price_table_comp_color padding_8">
                                  <FormattedMessage
                                    {...messages.environments}
                                  />
                                </div>
                              </td>
                              <td></td>
                              <td>
                                <div className="price_table_comp__quantity padding_8">{`${recommendedEnvironments}`}</div>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                        {/* Results below the table */}
                        <div id="RPA_ROI.price_toggle" className="mar4">
                          <p
                            id="RPA_ROI.estimated-cost"
                            className="RPA_ROI__ibm-type-e estmt-cost price_table_comp__bodycopy"
                          >
                            <FormattedMessage
                              {...messages.calculationMessage}
                            />
                            <sup className="RPA_ROI__sup">2</sup>
                          </p>
                          <p className="RPA_ROI__prices price_table_comp__price">
                            {`${roiValue}`}
                          </p>
                        </div>
                        {/* ROI Percentage */}
                        <div className="top-mar3">
                          <p
                            id="RPA_ROI.estimated-cost"
                            className="RPA_ROI__ibm-type-e estmt-cost price_table_comp__bodycopy"
                          >
                            <FormattedMessage
                              {...messages.roiPercentageMessage}
                            />
                          </p>
                          <p className="RPA_ROI__prices price_table_comp__price">
                            {`${roiPercentage}%`}
                          </p>
                        </div>
                        {/* Monthly savings */}
                        <div className="top-mar3">
                          <p
                            id="RPA_ROI.estimated-cost"
                            className="RPA_ROI__ibm-type-e estmt-cost price_table_comp__bodycopy"
                          >
                            <FormattedMessage
                              {...messages.monthlyTimeSavingsMessage}
                            />
                            <sup className="RPA_ROI__sup">2</sup>
                          </p>
                          <p className="RPA_ROI__prices price_table_comp__price">
                            {`${monthlyTimeSavings} `}
                            <FormattedMessage {...messages.hoursMessage} />
                          </p>
                        </div>
                        {/* Additional savings */}
                        <div className="top-mar3">
                          <p
                            id="RPA_ROI.estimated-cost"
                            className="RPA_ROI__ibm-type-e estmt-cost price_table_comp__bodycopy"
                          >
                            <FormattedMessage
                              {...messages.additionalSavingsMessage}
                            />
                          </p>
                          <p className="RPA_ROI__prices price_table_comp__price">
                            {`${reworkSavings} `}
                          </p>
                        </div>
                        <div id="RPA_ROI.price_toggle" className="mar4">
                          <ButtonSet className="RPA_ROI__stack-button btn-set mar4">
                            <Button
                              id="request_btn"
                              onClick={() => {
                                segmentTracking('CTA Clicked', {
                                  pageTitle: 'RPA Pricing Page',
                                  object: 'Pricing Page',
                                  resultValue: 'CTA clicked',
                                  CTA: 'Request a quote now',
                                  location: 'ROI Estimator',
                                  action: '',
                                  field: '',
                                });
                                setCookie();
                                localStorage.setItem('showRPAForm', 'true');
                              }}
                            >
                              <FormattedMessage {...messages.getQuoteButton} />
                            </Button>
                            &nbsp; &nbsp;
                            <Button
                              size="sm"
                              id="request_roi_btn"
                              href="#RPA_PE"
                              target="_self"
                              rel="noreferrer"
                              kind="secondary"
                              onClick={() => {
                                segmentTracking('CTA Clicked', {
                                  pageTitle: 'RPA Pricing Page',
                                  object: 'Pricing Page',
                                  resultValue: 'CTA clicked',
                                  CTA: 'Deployment options & pricing button',
                                  location: 'ROI Estimator',
                                  action: '',
                                  field: '',
                                });
                                setCookie();
                              }}
                            >
                              <FormattedMessage {...messages.gePEButton} />
                            </Button>
                          </ButtonSet>
                        </div>
                        {/* End display results */}
                      </div>
                      {/* Note related to suggested price  */}
                      <div id="footnote" className="div-only-media">
                        <div className="bx--row mar4">
                          <div className="bx--col-lg-9">
                            <div>
                              <Footnote numNote={2} />
                            </div>
                            <div className="footnote_text">
                              <FormattedMessage {...messages.footnoteOne2} />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        {/* Drawer when request is being triggered */}
        <NoteDrawer drawerOpen={drawerOpen} setDrawerOpen={setDrawerOpen} />
        <div
          id="PutDrawerToFront"
          className={`RPA_ROI-drawer
        ${drawerOpen ? 'opened' : 'closed'} RPA_ROI-drawer-transition`}
        >
          <div className="RPA_ROI-drawer-inner-container">
            <div className="bx--row">
              <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                <div className="RPA_ROI-drawer-close-container">
                  <Button
                    className="RPA_ROI-drawer-close-btn"
                    kind="ghost"
                    size="sm"
                    renderIcon={Close32}
                    onClick={() => {
                      setDrawerOpen(false);
                    }}
                  >
                    {' '}
                    <FormattedMessage {...messages.CloseText} />
                  </Button>
                </div>
              </div>
            </div>
            <div className="bx--row">
              <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                <div className="RPA_ROI-drawer-notes-container">
                  <div id="urx-form-container">
                    <div id={URX_FORM.INSTANCE_ID}></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default RPAROIEstimator;
